import { createFileRoute, Link } from '@tanstack/react-router'
import React, { useState } from 'react'
import { ArrowLeft, BarChart2 } from 'lucide-react'
import {
  FinancialPerformanceDashboard,
  DREPerformanceDashboard,
  BudgetPerformanceDashboard,
  ProjectionsPerformanceDashboard,
  ClientPerformanceDashboard,
} from '@/components/dashboards'

export const Route = createFileRoute('/_auth/design-system/analytics-showcase')({
  component: AnalyticsShowcasePage,
})

type DashboardType = 'financial' | 'dre' | 'budget' | 'projections' | 'client'

function AnalyticsShowcasePage() {
  const [activeDashboard, setActiveDashboard] = useState<DashboardType>('financial')

  const dashboards = [
    { id: 'financial' as DashboardType, label: 'Performance Financeira', description: 'Dashboard principal com KPIs financeiros' },
    { id: 'dre' as DashboardType, label: 'DRE', description: 'Demonstração do Resultado do Exercício' },
    { id: 'budget' as DashboardType, label: 'Orçamento', description: 'Controle de orçamento de treinamento' },
    { id: 'projections' as DashboardType, label: 'Projeções', description: 'Projeções financeiras trimestrais' },
    { id: 'client' as DashboardType, label: 'Cliente', description: 'Performance individual de cliente (3 anos)' }
  ]

  return (
    <div className="min-h-screen bg-brand-dark text-white">
      {/* Header */}
      <div className="bg-brand-dark-light border-b border-brand-gray/20">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center space-x-4 mb-6">
            <Link
              to="/design-system/analytics-dashboard"
              className="p-2 rounded-monumenta bg-brand-dark hover:bg-brand-dark-lighter transition-colors duration-200 text-brand-gray hover:text-white"
            >
              <ArrowLeft size={20} />
            </Link>
            <div>
              <h1 className="text-3xl font-display font-bold text-brand-primary">
                Analytics Showcase
              </h1>
              <p className="text-brand-gray">
                Galeria de dashboards com dados reais do MonuTrack
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">

        {/* Introduction */}
        <div className="mb-12">
          <div className="bg-brand-dark-light rounded-monumenta p-8 border border-brand-gray/10">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-brand-primary/10 rounded-monumenta">
                <BarChart2 className="text-brand-primary" size={32} />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-display font-semibold text-white mb-3">
                  6 Dashboards Prontos para Uso
                </h2>
                <p className="text-brand-gray mb-4">
                  Cada dashboard abaixo foi criado com dados reais das páginas existentes no MonuTrack.
                  Todos usam o mesmo componente base (AnalyticsPerformanceDashboard) mas com datasets diferentes.
                  O dashboard de Custos demonstra o novo recurso de drill-down com tabelas detalhadas.
                </p>
                <div className="flex items-center gap-3">
                  <div className="status-success">100% YOLO</div>
                  <div className="status-primary">Dados Reais</div>
                  <div className="status-info">Pronto para Produção</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dashboard Selector */}
        <div className="mb-8">
          <h3 className="text-lg font-display font-semibold text-white mb-4">
            Selecione um Dashboard
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {dashboards.map((dashboard) => (
              <button
                key={dashboard.id}
                onClick={() => setActiveDashboard(dashboard.id)}
                className={`
                  p-4 rounded-monumenta text-left transition-all duration-200
                  ${activeDashboard === dashboard.id
                    ? 'bg-brand-primary text-brand-dark border-2 border-brand-primary'
                    : 'bg-brand-dark-light text-white border-2 border-brand-gray/10 hover:border-brand-primary/50'
                  }
                `}
              >
                <div className="font-display font-semibold mb-1">{dashboard.label}</div>
                <div className={`text-xs ${activeDashboard === dashboard.id ? 'text-brand-dark/80' : 'text-brand-gray'}`}>
                  {dashboard.description}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Active Dashboard */}
        <div className="mb-16">
          {activeDashboard === 'financial' && <FinancialPerformanceDashboard />}
          {activeDashboard === 'dre' && <DREPerformanceDashboard />}
          {activeDashboard === 'budget' && <BudgetPerformanceDashboard />}
          {activeDashboard === 'projections' && <ProjectionsPerformanceDashboard />}
          {activeDashboard === 'client' && <ClientPerformanceDashboard />}
        </div>
      </div>
    </div>
  )
}
